import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import ServiceWizard from '../../components/services/ServiceWizard';
import { colors } from '../../lib/colors';
import { allServicesForExplorer as services, Service } from '../../data/servicesData';
import { XMarkIcon } from '@heroicons/react/24/solid';

export default function InsuredRetirementStrategy() {
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  const handleServiceSelect = (selectedService: Service) => {
    // console.log("Service selected in wizard:", selectedService);
    setSelectedService(selectedService.id); // Assuming id is a unique identifier
    setIsWizardOpen(false);
  };

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="insured-retirement-strategy-irs" />
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Insured Retirement Strategy (IRS)</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              A Powerful Approach to Generating Tax-Efficient Retirement Income & Preserving Your Estate
            </Text>
          </div>

          <div className="space-y-8">
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is the Insured Retirement Strategy (IRS)?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                The Insured Retirement Strategy (IRS) is a sophisticated financial planning technique that utilizes a permanent life insurance policy (such as Whole Life or Universal Life) to create a source of tax-free cash flow during retirement. It involves accumulating cash value within the policy on a tax-deferred basis and then accessing these funds through policy loans, which are typically not considered taxable income.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                This strategy allows you to supplement your retirement income without triggering immediate tax consequences often associated with withdrawing from traditional retirement accounts, while also maintaining a tax-free death benefit for your heirs or estate.
              </Text>
            </Card>

            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How the IRS Works: A Phased Approach</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Accumulation Phase:</strong> During your working years, you (or your corporation) pay premiums into a permanent life insurance policy. A portion of these premiums contributes to the policy's cash surrender value (CSV), which grows on a tax-deferred basis.
                </li>
                <li>
                  <strong>Retirement / Access Phase:</strong> Once you retire and need supplemental income, you arrange a collateral loan from a financial institution, using the accumulated cash value in your life insurance policy as security. The loan proceeds you receive are generally tax-free.
                </li>
                <li>
                  <strong>Estate Phase:</strong> Upon your passing, the tax-free death benefit from the life insurance policy is paid out. Typically, the outstanding loan balance plus accrued interest is repaid to the lender from the death benefit proceeds, with the remaining amount going to your designated beneficiaries or estate, tax-free.
                </li>
              </ol>
            </Card>

            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits of the Insured Retirement Strategy</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Tax-Efficient Retirement Income:</strong> Access funds via policy loans without triggering immediate taxation, providing a valuable supplement to other retirement income sources like pensions, RRSPs/RRIFs, or investments.</li>
                <li><strong>Estate Preservation & Enhancement:</strong> The policy's death benefit provides a tax-free lump sum to beneficiaries, helping to cover final expenses, estate taxes, or leaving a larger legacy, even after repaying the loan.</li>
                <li><strong>Tax-Deferred Growth:</strong> The cash value within the policy grows sheltered from annual taxation, allowing for potentially faster accumulation compared to fully taxed investments.</li>
                <li><strong>Potential Creditor Protection:</strong> Depending on the ownership structure and jurisdiction, life insurance policies can offer a degree of protection from creditors.</li>
                <li><strong>Flexibility:</strong> Policy loans do not typically require repayment during your lifetime, offering flexibility in managing cash flow.</li>
              </ul>
            </Card>

            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Understanding the Risks & Considerations</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">The IRS is a powerful strategy but requires careful planning and management:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Loan Interest Costs:</strong> Interest accrues on the policy loan and must be managed. If interest rates rise significantly or the loan balance grows too large relative to the cash value, it could impact the policy's long-term viability or reduce the net death benefit.</li>
                <li><strong>Policy Performance:</strong> The growth of the cash value, especially in Universal Life policies linked to market investments, is not guaranteed and can impact the amount available for loans.</li>
                <li><strong>Tax Implications on Lapse/Surrender:</strong> If the policy lapses or is surrendered while a loan is outstanding, the loan amount exceeding the policy's adjusted cost basis (ACB) could become taxable income.</li>
                <li><strong>Complexity:</strong> The IRS involves coordinating between an insurance policy and a loan facility, requiring professional advice and ongoing monitoring.</li>
                <li><strong>Qualification:</strong> Requires meeting both medical and financial underwriting for the life insurance policy and qualifying for the loan from a third-party lender.</li>
              </ul>
            </Card>

            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is the Insured Retirement Strategy Right for You?</Title>
              <Text className="text-gray-600 dark:text-gray-300 mb-3">This strategy is typically best suited for individuals, often business owners or high-income earners, who:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Have a long-term need for permanent life insurance coverage.</li>
                <li>Are looking for tax-efficient ways to supplement retirement income.</li>
                <li>Have sufficient, stable cash flow to fund the insurance premiums over the long term.</li>
                <li>Are comfortable with the concept of leverage and the associated risks.</li>
                <li>Prioritize estate preservation and leaving a tax-free legacy.</li>
                <li>Are in good health and can qualify for the necessary insurance coverage.</li>
              </ul>
            </Card>

            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Enhance Your Retirement with the IRS</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Could the Insured Retirement Strategy provide the tax-efficient income and estate benefits you're looking for? Contact us to explore if the IRS aligns with your financial future.
              </Text>
              <button
                onClick={() => setIsWizardOpen(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Find Your Solution
              </button>
            </div>
          </div>
        </div>
      </section>

      {isWizardOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center backdrop-blur-sm overflow-hidden p-4"
          onClick={() => setIsWizardOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full mx-auto animate-fadeIn overflow-hidden relative shadow-xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
            }}
          >
            <button
              onClick={() => setIsWizardOpen(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10 p-1 bg-white/10 dark:bg-black/10 rounded-full"
              aria-label="Close service wizard"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 4rem)' }}>
              <ServiceWizard
                services={services}
                onServiceSelect={handleServiceSelect}
                isOpen={isWizardOpen}
                onClose={() => setIsWizardOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </MarketingLayout>
  );
}